import { ETextAlignment, ETitlePosition, type SciChartSurface } from 'scichart';
import { toCanvas as htmlToCanvas } from 'html-to-image';
import {
  computeAutoFontFactor,
  downloadBlob,
  ELEMENT_SIZE_MULTIPLIER,
  mimeTypeFor,
  resolveOutputSize,
} from './utils';
import type {
  ChartImageExporter,
  ElementSize,
  ExportImageOptions,
  SciChartCaptureOptions,
  SciChartElementHooks,
} from './types';

// SciChart has no built-in image export (per its docs), so we snapshot the
// container with html-to-image and restore the mutated surface via a cleanup stack.

interface SciChartExporterConfig {
  getSurface: () => SciChartSurface | null;
  getContainer: () => HTMLElement | null;
  elementHooks?: SciChartElementHooks;
  suspendUpdates?: () => void;
  resumeUpdates?: () => void;
  captureOptions?: SciChartCaptureOptions;
  /**
   * An optional extra DOM element (e.g. a chart legend) to include in the export. It's a React
   * sibling of the WebGL canvas — outside the captured surface — so when present it's rasterized
   * separately and stacked ABOVE the chart image (see `composeWithOverlay`). Domain-neutral: the
   * caller maps its own element (a legend) to this generic slot.
   */
  getOverlayElement?: () => HTMLElement | null;
  /**
   * Optional text drawn under the title in the export, such as the figures
   * shown above the chart on the page. The parts go on one line when they fit
   * the image's width, and are wrapped between parts when they don't. It is
   * drawn even when the title is turned off.
   */
  getSubtitle?: () => string[] | null;
}

/**
 * Set on the chart's container while an export has it out of the page at the
 * image's size. Code that sizes the chart from the page should leave it alone
 * while this is set, or the image is drawn at the chart's size on the page.
 */
export const CHART_EXPORTING_ATTRIBUTE = 'data-chart-exporting';

// ─── Built-in capture defaults ────────────────────────────────────────────────
// Override any subset via SciChartExporterConfig.captureOptions.

const DEFAULT_BACKGROUND_COLOR = '#ffffff';
const DEFAULT_PIXEL_RATIO = 1;
// false so html-to-image reuses cached fonts/CSS across the freeze + capture
// passes and repeat exports (our assets are static, so busting only re-fetches).
const DEFAULT_CACHE_BUST = false;
const DEFAULT_JPEG_QUALITY = 0.95;
const DEFAULT_RENDER_WAIT_TIMEOUT_MS = 2000;
// Render passes (each after a real frame) to let a resized surface fully draw.
const DEFAULT_SETTLE_PASSES = 2;
// Per-size font-scale ceiling (SciChart-only): its layout can't reflow around
// oversized labels, so cap Large/XL on small charts; Small/Medium stay uncapped.
const FONT_FACTOR_MAX_BY_SIZE: Record<ElementSize, number> = {
  small: Infinity,
  medium: Infinity,
  large: 2.8,
  xlarge: 3.5,
};

// Export-only chart title, rendered natively by SciChart. Base size (px, at 1×)
// matches the Highcharts export title; scaled by the same fontFactor as the axes.
const TITLE_BASE_FONT_SIZE = 18;
const TITLE_TEXT_COLOR = '#161616';
// The gap between the title's lines, as a multiple of the line height.
const TITLE_LINE_SPACING = 1.5;
// What goes between two subtitle parts on the same line.
const SUBTITLE_SEPARATOR = '     ';

/**
 * Puts the parts on as few lines as fit the width, breaking only between parts.
 * A part wider than the width gets a line of its own.
 *
 * @param {string[]} parts - The text to lay out, in order.
 * @param {number} maxWidth - The widest a line can be, in px.
 * @param {string} font - The CSS font the text is drawn in.
 * @return {string[]} The lines.
 */
function wrapParts(parts: string[], maxWidth: number, font: string): string[] {
  const context = document.createElement('canvas').getContext('2d');
  if (!context) return parts.length > 0 ? [parts.join(SUBTITLE_SEPARATOR)] : [];

  context.font = font;
  const lines: string[] = [];
  for (const part of parts) {
    const last = lines[lines.length - 1];
    const joined = `${last}${SUBTITLE_SEPARATOR}${part}`;
    if (last !== undefined && context.measureText(joined).width <= maxWidth) {
      lines[lines.length - 1] = joined;
    } else {
      lines.push(part);
    }
  }
  return lines;
}

interface ResolvedCaptureOptions {
  backgroundColor: string | null;
  pixelRatio: number;
  cacheBust: boolean;
  jpegQuality: number;
  renderWaitTimeoutMs: number;
  hideSharedCanvasOnResize: boolean;
}

function resolveCaptureOptions(
  opts: SciChartCaptureOptions | undefined
): ResolvedCaptureOptions {
  return {
    // `null` is an explicit "transparent canvas" signal — preserve it instead
    // of falling back to white when the caller passed it.
    backgroundColor:
      opts && 'backgroundColor' in opts
        ? (opts.backgroundColor ?? null)
        : DEFAULT_BACKGROUND_COLOR,
    pixelRatio: opts?.pixelRatio ?? DEFAULT_PIXEL_RATIO,
    cacheBust: opts?.cacheBust ?? DEFAULT_CACHE_BUST,
    jpegQuality: opts?.jpegQuality ?? DEFAULT_JPEG_QUALITY,
    renderWaitTimeoutMs:
      opts?.renderWaitTimeoutMs ?? DEFAULT_RENDER_WAIT_TIMEOUT_MS,
    // Off by default so existing single-chart exports are unchanged; only the
    // headless/bulk capture path (content export) opts in.
    hideSharedCanvasOnResize: opts?.hideSharedCanvasOnResize ?? false,
  };
}

interface SciChartSurfaceLike {
  changeViewportSize?: (width: number, height: number) => void;
  invalidateElement?: (options?: { force?: boolean }) => void;
  // Native promise that resolves once the frame is copied to the 2D canvas.
  nextStateRender?: () => Promise<void>;
}

const asLike = (s: SciChartSurface): SciChartSurfaceLike =>
  s as unknown as SciChartSurfaceLike;

// Force a redraw, then await SciChart's native nextStateRender (resolves when the
// frame hits the 2D canvas); race a timeout so a stalled surface can't hang export.
function waitForRender(
  surface: SciChartSurfaceLike,
  timeoutMs: number
): Promise<void> {
  surface.invalidateElement?.({ force: true });
  const render = surface.nextStateRender?.();
  if (!render) {
    return new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    });
  }
  let timeoutId: number;
  const timeout = new Promise<void>((resolve) => {
    timeoutId = window.setTimeout(resolve, timeoutMs);
  });
  return Promise.race([render, timeout]).finally(() =>
    window.clearTimeout(timeoutId)
  );
}

// Run several render passes, each after a real animation frame, so a freshly
// resized surface fully draws before capture (a single pass is often incomplete).
async function settleRender(
  surface: SciChartSurfaceLike,
  timeoutMs: number,
  passes: number
): Promise<void> {
  for (let i = 0; i < passes; i += 1) {
    // Bound the frame wait — rAF pauses in a background tab and would otherwise
    // stall the export indefinitely.
    await new Promise<void>((resolve) => {
      const id = window.setTimeout(resolve, timeoutMs);
      requestAnimationFrame(() => {
        window.clearTimeout(id);
        resolve();
      });
    });
    await waitForRender(surface, timeoutMs);
  }
}

// A cold first export resizes the surface a few frames after changeViewportSize;
// poll (bounded) until the canvas backing reaches the target width before capture.
function waitForSurfaceResize(
  container: HTMLElement,
  targetWidth: number,
  timeoutMs: number
): Promise<void> {
  const deadline = Date.now() + timeoutMs;
  return new Promise((resolve) => {
    const check = () => {
      const canvases = Array.from(container.querySelectorAll('canvas'));
      const maxWidth = canvases.reduce((m, c) => Math.max(m, c.width), 0);
      if (maxWidth >= targetWidth - 1 || Date.now() >= deadline) resolve();
      else requestAnimationFrame(check);
    };
    requestAnimationFrame(check);
  });
}

const warnedContainers = new WeakSet<HTMLElement>();
function warnIfNotPositioned(container: HTMLElement): void {
  if (warnedContainers.has(container)) return;
  const computed =
    typeof window !== 'undefined' &&
    typeof window.getComputedStyle === 'function'
      ? window.getComputedStyle(container).position
      : '';
  if (computed === 'static' || computed === '') {
    warnedContainers.add(container);
    console.warn(
      '[chart-export] SciChart container is `position: static`. dom-to-image ' +
        'capture may miss absolutely-positioned overlays — set ' +
        '`position: relative` on the chart root to fix.'
    );
  }
}

// Wraps `canvas.toBlob` in a promise; mime + JPEG quality come from opts.
function canvasToBlob(
  canvas: HTMLCanvasElement,
  format: 'png' | 'jpeg',
  jpegQuality: number
): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => resolve(blob),
      mimeTypeFor(format),
      format === 'jpeg' ? jpegQuality : undefined
    );
  });
}

// Pin a snapshot of the chart over the live one for the capture, so the export's
// surface mutation is never visible. Best-effort: null if the snapshot fails.
async function mountFreezeOverlay(
  container: HTMLElement,
  capture: ResolvedCaptureOptions
): Promise<HTMLElement | null> {
  try {
    const rect = container.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    const snapshot = await htmlToCanvas(container, {
      pixelRatio: 1,
      cacheBust: capture.cacheBust,
      // SciChart text is canvas pixels, not DOM — skip the (slow) web-font embed.
      skipFonts: true,
      backgroundColor: capture.backgroundColor ?? undefined,
    });
    snapshot.style.width = '100%';
    snapshot.style.height = '100%';
    const overlay = document.createElement('div');
    overlay.style.cssText = [
      'position:fixed',
      `left:${rect.left}px`,
      `top:${rect.top}px`,
      `width:${rect.width}px`,
      `height:${rect.height}px`,
      'z-index:2147483646',
      'pointer-events:none',
    ].join(';');
    overlay.appendChild(snapshot);
    document.body.appendChild(overlay);
    return overlay;
  } catch {
    return null;
  }
}

/**
 * Stacks an extra DOM element (e.g. the chart legend) above the captured chart canvas so the export
 * includes it. The element is a React sibling of the SciChart canvas (custom-chart / spec-chart),
 * NOT part of the WebGL surface, so it's never in the container html-to-image rasterizes. Rendered
 * at the chart's scale (so it stays proportional + crisp on a resized export) with fonts embedded
 * (it's DOM text, unlike the SciChart canvas). Best-effort: any failure falls back to the chart-only
 * canvas so an overlay hiccup can never sink the export.
 *
 * @param {HTMLCanvasElement} chartCanvas - The rasterized chart image.
 * @param {HTMLElement} overlayEl - The DOM element to stack above the chart (e.g. a legend).
 * @param {ResolvedCaptureOptions} capture - Resolved capture options (background / cacheBust / pixelRatio).
 * @param {number} scale - The output/live width ratio, so the element rasterizes at the export scale.
 * @return {Promise<HTMLCanvasElement>} The combined canvas, or the chart canvas unchanged on failure.
 */
async function composeWithOverlay(
  chartCanvas: HTMLCanvasElement,
  overlayEl: HTMLElement,
  capture: ResolvedCaptureOptions,
  scale: number
): Promise<HTMLCanvasElement> {
  try {
    if (overlayEl.offsetWidth <= 0 || overlayEl.offsetHeight <= 0) {
      return chartCanvas;
    }
    const overlayCanvas = await htmlToCanvas(overlayEl, {
      pixelRatio: capture.pixelRatio * scale,
      backgroundColor: capture.backgroundColor ?? undefined,
      cacheBust: capture.cacheBust,
    });
    if (overlayCanvas.width === 0 || overlayCanvas.height === 0) {
      return chartCanvas;
    }
    const combined = document.createElement('canvas');
    combined.width = Math.max(chartCanvas.width, overlayCanvas.width);
    combined.height = overlayCanvas.height + chartCanvas.height;
    const ctx = combined.getContext('2d');
    if (!ctx) {
      return chartCanvas;
    }
    if (capture.backgroundColor) {
      ctx.fillStyle = capture.backgroundColor;
      ctx.fillRect(0, 0, combined.width, combined.height);
    }
    ctx.drawImage(overlayCanvas, 0, 0); // overlay band on top (matches its on-screen placement)
    ctx.drawImage(chartCanvas, 0, overlayCanvas.height); // chart below
    return combined;
  } catch {
    return chartCanvas;
  }
}

export function createSciChartExporter(
  config: SciChartExporterConfig
): ChartImageExporter {
  const {
    getSurface,
    getContainer,
    elementHooks,
    suspendUpdates,
    resumeUpdates,
    captureOptions,
    getOverlayElement,
    getSubtitle,
  } = config;
  const capture = resolveCaptureOptions(captureOptions);

  // Renders the chart to an image Blob at the requested options, restoring the live
  // surface afterwards. Shared by both public methods: `exportToBlob` returns it,
  // `exportAndDownload` downloads it. Resolves `null` when there's no chart / no size.
  const captureToBlob = async (
    opts: ExportImageOptions
  ): Promise<Blob | null> => {
    const surface = getSurface();
    const container = getContainer();
    if (!surface || !container) return null;

    const liveSize = {
      width: container.offsetWidth,
      height: container.offsetHeight,
    };
    const output = resolveOutputSize(opts, liveSize);
    if (!output) return null;

    // Fonts scale by `autoFactor × userFactor` (shared multiplier = Highcharts);
    // markers/lines by `userFactor`. The cap only bites Large/XL on small charts.
    const userFactor = ELEMENT_SIZE_MULTIPLIER[opts.elementSize];
    const autoFactor = computeAutoFontFactor(output.width, liveSize.width);
    const fontFactor = Math.min(
      autoFactor * userFactor,
      FONT_FACTOR_MAX_BY_SIZE[opts.elementSize]
    );

    // The live SciChart has no title (it's a sibling React header), so add one
    // for the export only via SciChart's native title API; restored in finally.
    const titleLabel = opts.chartLabel?.trim() ?? '';
    const titleEnabled = opts.elements.title !== false && titleLabel.length > 0;
    const titleFontSize = TITLE_BASE_FONT_SIZE * fontFactor;
    const titleLines = [
      ...(titleEnabled ? [titleLabel] : []),
      ...wrapParts(
        getSubtitle?.() ?? [],
        output.width - titleFontSize * 2,
        `${titleFontSize}px ${surface.titleStyle?.fontFamily ?? 'arial'}`
      ),
    ];

    // LIFO cleanup stack so the chart is restored even if capture throws.
    const cleanups: Array<{ label: string; run: () => void }> = [];

    // Pin a snapshot over the live chart so the export mutation is never visible.
    // Cover the clip parent (not just the container) so the whole region is hidden.
    const freezeTarget = container.parentElement ?? container;
    const freezeOverlay = await mountFreezeOverlay(freezeTarget, capture);

    try {
      if (suspendUpdates) {
        suspendUpdates();
        if (resumeUpdates) {
          cleanups.push({ label: 'resumeUpdates', run: resumeUpdates });
        }
      }

      if (elementHooks?.applyScaling) {
        elementHooks.applyScaling({ fontFactor, userFactor });
        if (elementHooks.restoreScaling) {
          const restore = elementHooks.restoreScaling;
          cleanups.push({ label: 'restoreScaling', run: restore });
        }
      }

      if (elementHooks?.applyVisibility) {
        elementHooks.applyVisibility(opts.elements);
        if (elementHooks.restoreVisibility) {
          const restore = elementHooks.restoreVisibility;
          cleanups.push({ label: 'restoreVisibility', run: restore });
        }
      }

      const like = asLike(surface);
      const sizeChanged =
        output.width !== liveSize.width || output.height !== liveSize.height;
      if (sizeChanged) {
        container.setAttribute(CHART_EXPORTING_ATTRIBUTE, '');
        cleanups.push({
          label: 'removeExportingAttribute',
          run: () => container.removeAttribute(CHART_EXPORTING_ATTRIBUTE),
        });

        // SciChart (useSharedCache) renders through ONE shared WebGL canvas that
        // lives OUTSIDE this chart's container and copies each frame to the per-chart
        // 2D canvas we actually capture. Growing the surface to the output size grows
        // that shared canvas too, and it briefly paints full-size at its own location
        // — a flash the per-chart freeze overlay can't cover. Hide it for the capture
        // window; `visibility:hidden` keeps the GPU render + copy-to-2D working (unlike
        // `display:none`). Guarded to the EXTERNAL shared canvas so we never blank the
        // 2D canvas inside this container that the capture reads. Opt-in
        // (`hideSharedCanvasOnResize`) so existing single-chart exports are untouched.
        if (capture.hideSharedCanvasOnResize) {
          const webglCanvas = (
            surface as unknown as { domCanvasWebGL?: HTMLCanvasElement }
          ).domCanvasWebGL;
          if (webglCanvas && !container.contains(webglCanvas)) {
            const savedVisibility = webglCanvas.style.visibility;
            webglCanvas.style.visibility = 'hidden';
            // Pushed BEFORE the resize/CSS restores below, so it runs LAST in the
            // LIFO drain — the canvas is revealed only after the viewport shrinks
            // back to live size. Deliberately a guaranteed cleanup (not a deferred
            // post-settle callback): leaving the SHARED canvas hidden on an error
            // path would blank every chart, not just this one.
            cleanups.push({
              label: 'restoreWebglCanvasVisibility',
              run: () => {
                webglCanvas.style.visibility = savedVisibility;
              },
            });
          }
        }

        // Grow an element to the output size, saving its inline CSS for restore.
        const growToOutput = (el: HTMLElement, label: string) => {
          const saved = el.style.cssText;
          el.style.setProperty('width', `${output.width}px`, 'important');
          el.style.setProperty('height', `${output.height}px`, 'important');
          cleanups.push({
            label,
            run: () => {
              el.style.cssText = saved;
            },
          });
        };

        // Pin the container's parent (size + clip + anchor) so taking the
        // container out of flow at the output size can't reflow the page.
        const containerParent = container.parentElement;
        if (containerParent) {
          const savedParentCss = containerParent.style.cssText;
          containerParent.style.width = `${containerParent.offsetWidth}px`;
          containerParent.style.height = `${containerParent.offsetHeight}px`;
          containerParent.style.overflow = 'hidden';
          if (getComputedStyle(containerParent).position === 'static') {
            containerParent.style.position = 'relative';
          }
          cleanups.push({
            label: 'restoreContainerParentCss',
            run: () => {
              containerParent.style.cssText = savedParentCss;
            },
          });
        }

        // Out of flow at the output size (in-flow, flex clamps it to the live
        // size and stales the capture); top/left stay 0 or html-to-image clips the bottom.
        const savedContainerCss = container.style.cssText;
        container.style.setProperty('position', 'absolute', 'important');
        container.style.setProperty('top', '0', 'important');
        container.style.setProperty('left', '0', 'important');
        container.style.setProperty('width', `${output.width}px`, 'important');
        container.style.setProperty(
          'height',
          `${output.height}px`,
          'important'
        );
        cleanups.push({
          label: 'restoreContainerCss',
          run: () => {
            container.style.cssText = savedContainerCss;
          },
        });

        // The SciChart canvas fills its mount host (sized 100%); grow every
        // element between the canvas and the container so it isn't clipped.
        const canvasEl = container.querySelector('canvas');
        if (canvasEl) {
          let el = canvasEl.parentElement;
          while (el && el !== container && container.contains(el)) {
            growToOutput(el, `restoreAncestorCss:${el.id || el.tagName}`);
            el = el.parentElement;
          }
        }

        // Grow the WebGL surface synchronously to match so the captured pixels
        // are crisp (not an upscaled small buffer).
        if (like.changeViewportSize) {
          const restoreSize = like.changeViewportSize;
          like.changeViewportSize(output.width, output.height);
          cleanups.push({
            label: 'restoreViewportSize',
            run: () =>
              restoreSize.call(surface, liveSize.width, liveSize.height),
          });
        }

        // Wait for the resize to land before settling — a cold first export
        // lands it late, else we'd capture a live-sized chart in the corner.
        await waitForSurfaceResize(
          container,
          output.width,
          capture.renderWaitTimeoutMs
        );
      }

      // Settle the resize before injecting the title, so the title lays out
      // against the final output size (setting surface.title auto-invalidates).
      await settleRender(
        like,
        capture.renderWaitTimeoutMs,
        DEFAULT_SETTLE_PASSES
      );

      if (titleLines.length > 0) {
        const originalTitle = surface.title;
        const originalTitleStyle = surface.titleStyle;
        surface.title = titleLines.length === 1 ? titleLines[0] : titleLines;
        surface.titleStyle = {
          ...originalTitleStyle,
          position: ETitlePosition.Top,
          alignment: ETextAlignment.Center,
          color: TITLE_TEXT_COLOR,
          fontSize: titleFontSize,
          // At SciChart's default spacing, the title and subtitle lines touch.
          lineSpacing: TITLE_LINE_SPACING,
        };
        cleanups.push({
          label: 'restoreTitle',
          run: () => {
            surface.title = originalTitle;
            surface.titleStyle = originalTitleStyle;
          },
        });

        // The title texture builds over a couple of render passes; settle again
        // so the first export isn't a stale, title-less frame.
        await settleRender(
          like,
          capture.renderWaitTimeoutMs,
          DEFAULT_SETTLE_PASSES
        );
      }

      warnIfNotPositioned(container);
      // Surface is already output-sized, so capture at 1× (higher pixelRatio just
      // blurs). backgroundColor null keeps PNG alpha; white avoids black JPEGs.
      const canvas = await htmlToCanvas(container, {
        width: output.width,
        height: output.height,
        pixelRatio: capture.pixelRatio,
        backgroundColor: capture.backgroundColor ?? undefined,
        cacheBust: capture.cacheBust,
        // SciChart text is canvas pixels, not DOM — skip the (slow) web-font embed.
        skipFonts: true,
      });
      // Stack the overlay element (e.g. the legend — a DOM sibling outside the captured canvas)
      // above the chart when the caller provides one. Domain toggling (e.g. a Legend on/off) stays
      // in the caller: it returns null here to omit the element.
      const overlayEl = getOverlayElement?.();
      const scale = liveSize.width > 0 ? output.width / liveSize.width : 1;
      const finalCanvas = overlayEl
        ? await composeWithOverlay(canvas, overlayEl, capture, scale)
        : canvas;
      const blob = await canvasToBlob(
        finalCanvas,
        opts.outputFormat,
        capture.jpegQuality
      );
      return blob;
    } finally {
      while (cleanups.length > 0) {
        const { label, run } = cleanups.pop()!;
        try {
          run();
        } catch (err) {
          // Best-effort restore — log so failures stay debuggable.
          console.warn(
            `[chart-export] sciChart cleanup "${label}" failed`,
            err
          );
        }
      }
      // Keep the snapshot up until the live chart repaints at its restored size,
      // so it never flashes the export frame back. settleRender is bounded, so
      // this always resolves — no fixed fallback timer that could yank it early.
      if (freezeOverlay) {
        const overlay = freezeOverlay;
        void settleRender(
          asLike(surface),
          capture.renderWaitTimeoutMs,
          DEFAULT_SETTLE_PASSES
        ).finally(() => overlay.remove());
      }
    }
  };

  return {
    getLiveSize: () => {
      const container = getContainer();
      if (
        !container ||
        container.offsetWidth <= 0 ||
        container.offsetHeight <= 0
      )
        return null;
      return { width: container.offsetWidth, height: container.offsetHeight };
    },

    getElements: () => elementHooks?.getElements?.() ?? [],

    exportToBlob: (opts) => captureToBlob(opts),

    exportAndDownload: async (opts, filename) => {
      const blob = await captureToBlob(opts);
      if (!blob) return false;
      downloadBlob(blob, filename);
      return true;
    },
  };
}
